import { buildWabunkaTagViewModel, createDefaultWabunkaTagViewModel, type WabunkaTagViewModel } from '@/models/wabunka/wabunkaTag';
import { EXCLUDED_THEME_TAG_GROUP_NAMES, EXPERIENCE_CARD_THEME_TAG_MAX_DISPLAY } from '@/wabunka-src/features/Experience/constants/themeTagFilter';

import type { WabunkaExperienceTag } from '@japan-culture-and-technology/wabunka-protos/ts_proto/wabunka_tag';

/**
 * 体験に紐づくWabunka タグビューモデル
 */
export interface WabunkaExperienceTagViewModel {
  /** 体験マスタID */
  experienceId: string;
  /** タグ */
  tag: WabunkaTagViewModel;
}

/**
 * Wabunka タグビューモデル のデフォルト値を返却する
 *
 * @returns {WabunkaExperienceTagViewModel} - Wabunka タグビューモデル のデフォルト値
 */
export const createDefaultWabunkaExperienceTagViewModel = (): WabunkaExperienceTagViewModel => ({
  experienceId: '',
  tag: createDefaultWabunkaTagViewModel(),
});

/**
 * APIレスポンスからビューモデルを作成する
 *
 * @param {WabunkaTag} res - APIレスポンス
 * @returns {WabunkaTagViewModel} - ビューモデル
 */
export const buildWabunkaExperienceTagViewModel = (res: WabunkaExperienceTag | undefined): WabunkaExperienceTagViewModel => {
  if (!res) return createDefaultWabunkaExperienceTagViewModel();
  return {
    experienceId: res.experienceId,
    tag: buildWabunkaTagViewModel(res.tag),
  };
};

/**
 * リスト表示用のビューモデル
 */
export type WabunkaExperienceTagListViewModel = WabunkaExperienceTagViewModel[];

/**
 * リスト表示用のビューモデルを組み立てる
 */
export const buildWabunkaExperienceTagListViewModel = (tags: WabunkaExperienceTag[] | undefined): WabunkaExperienceTagListViewModel => {
  if (!tags) {
    return [];
  }

  return tags.map(buildWabunkaExperienceTagViewModel);
};

// （Other places (エリア名)』のnameの場合は、エリア名のみを返す
export const displayDestinationTagName = (tags: WabunkaExperienceTagListViewModel): string => {
  // 行き先を表すタグは運用上１つのみのため、最初の要素を取得している
  const destinationTag = tags.find((tag) => tag.tag.tagGroupType === 'destination');
  // 行き先タグが見つかった場合、その名前を返し、見つからない場合は空文字を返す
  if (!destinationTag) return '';

  // （Other places (エリア名)』のnameの場合は、エリア名のみを返す
  const match = destinationTag.tag.name.match(/^Other places \((.*?)\)$/);
  return match ? match[1] : destinationTag.tag.name;
};

const EXCLUDED_THEME_TAG_GROUP_NAME_SET: ReadonlySet<string> = new Set<string>(EXCLUDED_THEME_TAG_GROUP_NAMES);

/**
 * 共通体験カードに表示するテーマタグを体験の `tags` から抽出する。
 *
 * 各タグは所属する全タググループ（`tag.tagGroups`、バックエンドが JCAT-4637 で全件返す）を持つ。
 * その中に「`type === 'theme'` かつ {@link EXCLUDED_THEME_TAG_GROUP_NAMES} に含まれないグループ」が
 * 1 件でもあれば表示対象とする。これにより、除外グループ（Seasonal Highlights / Trending Now）と
 * 非除外グループの両方に属するタグを過剰除外しない。
 *
 * - 同一 `tag.id` の重複は最初の出現のみ残す
 * - `tagGroups` が空（旧 backend で未充填）のタグは表示対象外（過剰表示より安全側に倒す）
 * - 受け取り順のまま、最大 {@link EXPERIENCE_CARD_THEME_TAG_MAX_DISPLAY} 件で打ち切る
 *
 * JCAT-4637 / JCAT-4853 連動仕様。
 */
export const extractDisplayThemeTags = (tags: WabunkaExperienceTagListViewModel): WabunkaTagViewModel[] => {
  const result: WabunkaTagViewModel[] = [];
  const seenIds = new Set<string>();
  for (const t of tags) {
    if (seenIds.has(t.tag.id)) continue;
    seenIds.add(t.tag.id);

    // theme かつ非除外グループに 1 件でも所属していれば表示する（tagGroups 未充填時は空配列扱い）
    const hasDisplayableThemeGroup = (t.tag.tagGroups ?? []).some((g) => g.type === 'theme' && !EXCLUDED_THEME_TAG_GROUP_NAME_SET.has(g.name));
    if (!hasDisplayableThemeGroup) continue;

    result.push(t.tag);
  }

  return result.slice(0, EXPERIENCE_CARD_THEME_TAG_MAX_DISPLAY);
};
